import { useState, useCallback, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import AgentPageLayout from '../components/shared/AgentPageLayout';
import ItemSidebar from '../components/shared/ItemSidebar';
import Workspace from '../components/shared/Workspace';
import DataTable from '../components/shared/DataTable';
import DrillDownPanel from '../components/shared/DrillDownPanel';
import KpiChat from '../components/kpi/KpiChat';
import KpiToolbar from '../components/kpi/KpiToolbar';
import { useKpiList } from '../components/kpi/useKpiList';
import { kpiApi } from '../services/kpiApi';
import { useCompany } from '../contexts/CompanyContext';

export default function KpiPage() {
  const { selectedCompanies, addCompany, removeCompany } = useCompany();

  // KPI list (for ItemSidebar)
  const { items: kpiItems, kpiMap, refresh: refreshKpis } = useKpiList();

  // KPI selection
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [threadId, setThreadId] = useState(null);

  // Toolbar config — initialized from KPI query_config when selected.
  // summary_by is a single string: "TOTAL" | "MONTH" | "QUARTER" | "YEAR" | "BY_<DIM>".
  const [toolbarConfig, setToolbarConfig] = useState({
    date_range: null,
    summary_by: 'TOTAL',
    filters: [],
  });

  // Results
  const [results, setResults] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Drill-down
  const [drillCell, setDrillCell] = useState(null);

  // Cube dimensions (fetched once)
  const [dimensions, setDimensions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const meta = await kpiApi.getCubeMeta();
        const cubes = meta?.data?.cubes || meta?.cubes || [];
        const tiCube = cubes.find(c => c.name === 'transaction_items');
        if (tiCube) {
          const dims = tiCube.dimensions
            .map(d => ({ value: d.name.replace('transaction_items.', ''), label: d.title || d.shortTitle || d.name.replace('transaction_items.', '') }))
            .filter(d => !d.value.includes('_id') && !d.value.includes('rf_id') && d.value !== 'transaction_date');
          setDimensions(dims);
        }
      } catch { /* use fallback */ }
    })();
  }, []);

  // ItemSidebar onSelectItem passes item id — look up full KPI from kpiMap
  const handleSelectItem = useCallback((id) => {
    const kpi = kpiMap.get(id);
    if (!kpi) return;
    setSelectedKpi(kpi);
    setResults(null);
    setError(null);
    const qc = kpi.query_config || {};
    setToolbarConfig({
      date_range: qc.date_range || null,
      summary_by: qc.summary_by || 'TOTAL',
      filters: qc.filters || [],
    });
  }, [kpiMap]);

  // Execute KPI
  const handleExecute = useCallback(async () => {
    if (!selectedKpi || !selectedCompanies.length) return;
    setLoading(true);
    setError(null);
    try {
      const body = {
        rf_ids: selectedCompanies.map(c => c.rf_id),
      };
      if (toolbarConfig.date_range?.[0] && toolbarConfig.date_range?.[1]) {
        body.date_range = toolbarConfig.date_range;
      }
      // summary_by is the single unified axis string consumed by data-engine.
      body.summary_by = toolbarConfig.summary_by || 'TOTAL';
      if (toolbarConfig.filters?.length) {
        const validFilters = toolbarConfig.filters.filter(f => f.dimension);
        if (validFilters.length) body.filters = validFilters;
      }

      const data = await kpiApi.executeKpi(selectedKpi.id, body);
      setResults(data.rows || []);
      setColumns(data.columns || []);
    } catch (err) {
      setError(err.message);
      setResults(null);
    }
    setLoading(false);
  }, [selectedKpi, selectedCompanies, toolbarConfig]);

  // Handle workspace actions from agent
  const handleWorkspaceAction = useCallback((action) => {
    if (action.action === 'refresh_view_list') refreshKpis();
  }, [refreshKpis]);

  // Build parentTimeDimensions for DrillDownPanel.
  // A time summary_by (MONTH/QUARTER/YEAR) maps to a Cube time granularity.
  const SUMMARY_BY_GRANULARITY = { MONTH: 'month', QUARTER: 'quarter', YEAR: 'year' };
  const summaryGranularity = SUMMARY_BY_GRANULARITY[toolbarConfig.summary_by];
  const parentTimeDimensions = summaryGranularity
    ? [{ dimension: 'transaction_items.transaction_date', granularity: summaryGranularity, dateRange: toolbarConfig.date_range }]
    : toolbarConfig.date_range
      ? [{ dimension: 'transaction_items.transaction_date', dateRange: toolbarConfig.date_range }]
      : [];

  const isFormula = selectedKpi?.source_type === 'formula';

  return (
    <AgentPageLayout
      sidebar={
        <ItemSidebar
          title="KPIs"
          items={kpiItems}
          activeId={selectedKpi?.id}
          onSelectItem={handleSelectItem}
          chatItemName={selectedKpi?.name || 'New Chat'}
          chat={
            <KpiChat
              selectedCompanies={selectedCompanies}
              onSelectCompany={addCompany}
              onRemoveCompany={(rfId) => removeCompany(rfId)}
              onWorkspaceAction={handleWorkspaceAction}
              onThreadCreated={setThreadId}
              threadId={threadId}
            />
          }
        />
      }
    >
      <Workspace
        title={selectedKpi ? selectedKpi.name : 'KPIs'}
        description={selectedKpi?.description}
        toolbar={
          selectedKpi && (
            <KpiToolbar
              config={toolbarConfig}
              onChange={setToolbarConfig}
              onExecute={handleExecute}
              loading={loading}
              isFormula={isFormula}
              dimensions={dimensions}
            />
          )
        }
      >
        {/* Error banner */}
        {error && (
          <div className="mx-4 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-zinc-400" size={24} />
          </div>
        )}

        {/* Results table */}
        {!loading && results && results.length > 0 && (
          <DataTable
            columns={columns}
            rows={results}
            mode="flat"
            onCellClick={(cell) => setDrillCell(cell)}
          />
        )}

        {/* Empty states */}
        {!loading && results && results.length === 0 && (
          <div className="flex items-center justify-center py-20 text-zinc-500 text-sm">
            No results. Try adjusting your date range or filters.
          </div>
        )}

        {!loading && !results && !error && (
          <div className="flex items-center justify-center py-20 text-zinc-500 text-sm">
            {selectedKpi ? 'Select a date range and click Execute.' : 'Select a KPI from the sidebar.'}
          </div>
        )}
      </Workspace>

      {/* Drill-down panel */}
      <DrillDownPanel
        open={!!drillCell}
        cell={drillCell}
        onClose={() => setDrillCell(null)}
        rfIds={selectedCompanies.map(c => c.rf_id)}
        parentTimeDimensions={parentTimeDimensions}
      />
    </AgentPageLayout>
  );
}
